import { gambits } from '@/core/gambits';
import type {
  MapMarkerData,
  OpenTraceResponse,
  PreviewTraceResponse,
  TraceData,
} from '@/core/network/api/map-puzzle/map-puzzle.api';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useMapPuzzleStore = defineStore(GamStoreId.MAP_PUZZLE, () => {
  const isLoading = ref(false);

  function flipMarkerToGreen(markerId: string) {
    const idx = markers.value.findIndex((m) => m.id === markerId);
    if (idx !== -1) {
      markers.value[idx] = { ...markers.value[idx], puzzleType: 'walk_solved' };
    }
  }

  async function openTraceById(traceId: string) {
    try {
      const res = await gambits.mapPuzzleApi.openTrace(traceId, 0, 0);
      const data = res.data?.data;
      if (data) {
        activeTrace.value = data;
      }
      return data ?? null;
    } catch {
      return null;
    }
  }

  function reset() {
    markers.value = [];
    mapMarkerExpiresAt.value = null;
    isLoading.value = false;
  }

  // --- Map markers ---

  const markers = ref<MapMarkerData[]>([]);
  const mapMarkerExpiresAt = ref<string | null>(null);

  const markersExpired = computed(() => {
    if (!mapMarkerExpiresAt.value) return true;
    return new Date(mapMarkerExpiresAt.value).getTime() <= Date.now();
  });

  function checkExpiration() {
    const now = Date.now();
    if (mapMarkerExpiresAt.value && new Date(mapMarkerExpiresAt.value).getTime() <= now) {
      markers.value = [];
      mapMarkerExpiresAt.value = null;
      reloadTracesFromCurrentLocation();
    } else {
      const alive = markers.value.filter((m) => new Date(m.expiresAt).getTime() > now);
      if (alive.length !== markers.value.length) {
        markers.value = alive;
        if (alive.length === 0) mapMarkerExpiresAt.value = null;
      }
    }
    traces.value = traces.value.filter((t) => !t.expiresAt || new Date(t.expiresAt).getTime() > now);
  }

  // --- Traces ---

  const traces = ref<TraceData[]>([]);
  const activeTrace = ref<OpenTraceResponse | null>(null);
  const previewedTrace = ref<PreviewTraceResponse | null>(null);
  const showTracePuzzle = ref(false);

  function reloadTracesFromCurrentLocation() {
    const loc = useGeolocationStore().userLocation?.location;
    if (loc) loadTraces(loc.lat, loc.lng);
  }

  async function loadTraces(lat: number, lng: number) {
    try {
      const res = await gambits.mapPuzzleApi.getTraces(lat, lng);
      const all = res.data?.data?.traces ?? [];

      const markerPuzzleIds = new Set(markers.value.map((m) => m.puzzleId));
      traces.value = all.filter((t) => {
        if (t.solvedByMe) return false;
        if (t.puzzleId && markerPuzzleIds.has(t.puzzleId)) {
          return t.isMine && t.status === 'trace_solved';
        }
        return true;
      });

      const traceMarkerPuzzleIds = new Set(
        markers.value.filter((m) => m.puzzleType === 'trace').map((m) => m.puzzleId),
      );
      const mySolvedTracePuzzleIds = new Set(
        traces.value
          .filter((t) => t.isMine && t.status === 'trace_solved' && t.puzzleId && !traceMarkerPuzzleIds.has(t.puzzleId))
          .map((t) => t.puzzleId),
      );
      if (mySolvedTracePuzzleIds.size > 0) {
        markers.value = markers.value.filter((m) => m.puzzleId == null || !mySolvedTracePuzzleIds.has(m.puzzleId));
      }
    } catch {
      traces.value = [];
    }
  }

  async function openTrace(traceId: string, lat: number, lng: number) {
    try {
      const res = await gambits.mapPuzzleApi.openTrace(traceId, lat, lng);
      const data = res.data?.data;
      if (data) {
        activeTrace.value = data;
        showTracePuzzle.value = true;
      }
      return data ?? null;
    } catch {
      return null;
    }
  }

  async function solveTrace(traceId: string, moveUci: string) {
    try {
      const res = await gambits.mapPuzzleApi.solveTrace(traceId, moveUci);
      const data = res.data?.data;
      if (data) {
        if (data.isCorrect) {
          const at = activeTrace.value;
          if (at) {
            markers.value.push({
              id: `trace-${at.id}`,
              lat: data.trace.lat,
              lng: data.trace.lng,
              puzzleId: at.puzzle.id,
              fen: at.puzzle.fen,
              solutionUci: `${at.puzzle.setupMove} ${at.puzzle.solutionMove}`,
              rating: at.puzzle.rating,
              puzzleType: 'trace',
              expiresAt: data.trace.expiresAt ?? new Date(Date.now() + 60 * 60 * 1000).toISOString(),
            });
          }
          traces.value = traces.value.filter((t) => t.id !== traceId);
          activeTrace.value = null;
        } else {
          const idx = traces.value.findIndex((t) => t.id === traceId);
          if (idx !== -1) traces.value[idx] = data.trace;
        }
      }
      return data ?? null;
    } catch {
      return null;
    }
  }

  async function previewMyTrace(traceId: string) {
    try {
      const res = await gambits.mapPuzzleApi.previewTrace(traceId);
      const data = res.data?.data;
      if (data) previewedTrace.value = data;
      return data ?? null;
    } catch {
      return null;
    }
  }

  async function viewTrace(traceId: string) {
    try {
      const res = await gambits.mapPuzzleApi.viewTrace(traceId);
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  }

  function closeTracePuzzle() {
    activeTrace.value = null;
    previewedTrace.value = null;
    showTracePuzzle.value = false;
  }

  return {
    isLoading,
    reset,
    // Map markers
    markers,
    mapMarkerExpiresAt,
    markersExpired,
    checkExpiration,
    flipMarkerToGreen,
    // Traces
    traces,
    activeTrace,
    previewedTrace,
    showTracePuzzle,
    loadTraces,
    openTrace,
    openTraceById,
    solveTrace,
    previewMyTrace,
    viewTrace,
    closeTracePuzzle,
    reloadTracesFromCurrentLocation,
  };
});
